/**
 * Report Dashboard — embed Power BI report.
 * Hiển thị cho tất cả sub-nav trong nhóm Report (/report/*).
 */
import { Box, Typography, Button } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { navigation } from '../config/navigation'
import type { NavItem } from '../config/navigation'

const PBI_EMBED_URL =
  'https://app.powerbi.com/reportEmbed?reportId=e0dd7ca2-d187-4902-8283-aac85678736f&autoAuth=true&ctid=6a4eb98c-7e75-4328-8d74-2bb5a18116be'

function findLabel(items: NavItem[], pathname: string): string[] {
  for (const item of items) {
    if (!item.children && item.path === pathname) return [item.label]
    if (item.children) {
      const sub = findLabel(item.children, pathname)
      if (sub.length > 0) return [item.label, ...sub]
    }
  }
  return []
}

export default function ReportDashboardPage() {
  const location = useLocation()
  const crumbs = findLabel(navigation, location.pathname)
  const title = crumbs.length > 0 ? crumbs[crumbs.length - 1] : 'Report'
  const canEmbedInCurrentContext =
    typeof window !== 'undefined' &&
    // Power BI sign-in trong iframe cần secure context; HTTP thường gây crypto/isInIframe errors.
    (window.isSecureContext || window.location.hostname === 'localhost')

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {crumbs.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {crumbs.slice(0, -1).join(' / ')}
        </Typography>
      )}
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        sx={{ mb: 1.5, alignSelf: 'flex-start' }}
        href={PBI_EMBED_URL}
        target="_blank"
        rel="noreferrer"
      >
        Open report in new tab
      </Button>
      {!canEmbedInCurrentContext && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }}>
          The current environment does not support Power BI login in iframe (HTTP/insecure context).
          Please open the report by the button above or access the page using HTTPS.
        </Typography>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {canEmbedInCurrentContext ? (
          <iframe
            title="PBI_ERP"
            src={PBI_EMBED_URL}
            style={{ width: '100%', height: '100%', minHeight: 540, border: 'none' }}
            allowFullScreen
          />
        ) : (
          <Box
            sx={{
              minHeight: 540,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Embed is disabled to prevent Power BI login errors on HTTP.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
